import { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { Form, message, Modal } from 'antd';
import { calculateImpressions } from '../../platform/ux/CampaignRequestor/CampaignRequestorUtil';
import { useCampaignRequestorContext } from '../components/campaignRequestor/useCampaignRequestorContext';

export const useCampaignForm = (mode = 'edit') => {
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form) || {};
  const [forceUpdateKey, setForceUpdateKey] = useState(0);
  const { selectedAdvertiser, setSelectedAdvertiser, childrenOrg } = useCampaignRequestorContext();

  // Refs for tracking previous values and initial load state
  const prevAdvertiserRef = useRef(formValues.advertiserId);
  const prevTypeRef = useRef(formValues.type);
  const isInitialLoadRef = useRef(true);
  const isCloningRef = useRef(false);

  // Derived state calculations
  const derivedState = useMemo(() => {
    const { budget = 0, cpm = 7, campaigns = [] } = formValues;
    
    return {
      impressions: calculateImpressions({ budget, cpm }),
      totalLineItemBudget: campaigns.reduce((sum, campaign) => 
        sum + (Number(campaign.budgetTotal) || 0), 0),
      lineItemCount: campaigns.length,
      isValidBudgetAllocation: budget >= campaigns.reduce((sum, campaign) => 
        sum + (Number(campaign.budgetTotal) || 0), 0),
      // Enhanced line item calculations
      campaigns: campaigns.map(campaign => ({
        ...campaign,
        budgetPercentage: campaign.budgetTotal && budget 
          ? Math.round((campaign.budgetTotal / budget) * 100) 
          : 0,
        impressions: calculateImpressions({
          budget: campaign.budgetTotal || 0,
          cpm: campaign.cpm || 7
        })
      })),
    };
  }, [formValues]);

  // Validation state
  const validationState = useMemo(() => {
    const validation = {
      hasRequiredDetails: !!(formValues.advertiserId && formValues.name && formValues.IONumber),
      hasValidBudget: !!(formValues.budget > 0 && formValues.cpm > 0),
      hasValidDates: !!(formValues.startDate && formValues.endDate),
      hasValidCategories: (() => {
        // Category is always required
        if (!formValues.category) return false;

        // For regulated categories (IAB7-5), advertiserType is also required
        if (formValues.category === 'IAB7-5') {
          return !!(formValues.advertiserType);
        }

        // For non-regulated categories, only category is required
        return true;
      })(),
      hasValidLineItems: (formValues.campaigns || []).every(campaign => {
        // Basic line item requirements
        const hasName = !!(campaign.name && campaign.name.trim());
        const hasValidBudget = !!(campaign.budgetTotal > 0);
        const hasTargetingType = !!(campaign.targetingType);
        const hasValidDates = !!(campaign.startDate && campaign.endDate);

        // Check if targeting is properly configured based on type
        let hasValidTargeting = false;
        if (campaign.targetingType === 'COUNTRY') {
          hasValidTargeting = !!(campaign.targetingCountries);
        } else if (campaign.targetingType === 'REGION') {
          hasValidTargeting = !!(campaign.targetingRegions && campaign.targetingRegions.length > 0);
        } else if (campaign.targetingType === 'LOCAL') {
          hasValidTargeting = !!(campaign.targetingLocations && campaign.targetingLocations.length > 0);
        }

        // Ad group/creatives validation
        const hasCreatives = !!(campaign.adGroup?.adGroupCreatives?.length > 0);

        return hasName && hasValidBudget && hasTargetingType && hasValidDates && hasValidTargeting && hasCreatives;
      }),
      hasValidClickthroughUrls: (formValues.campaigns || []).every(campaign => {
        // For video campaigns (type 8), clickthrough URLs are optional
        const isVideoType = formValues.type === 8;
        if (isVideoType) return true;

        // For display campaigns, clickthrough URLs are required
        return campaign.adGroup?.adGroupCreatives?.every(creative =>
          creative.clickthrough && creative.clickthrough.trim() !== ''
        ) || false;
      }),
      hasValidAttributionReporting: (() => {
        // If it's an awareness-only campaign, no KPI measurements are required
        if (formValues.awarenessCampaign === true) {
          return true;
        }

        // For non-awareness campaigns, at least one KPI measurement must be configured
        // Helper function to check if a config is valid
        const isValidConfig = (config) => {
          // Config must exist and not be null
          if (!config || config === null) {
            return false;
          }

          // Config must have either an ID (existing saved config) or a tag
          return !!(config.id || config.tag?.id || config.tag);
        };

        const hasTransactions = isValidConfig(formValues.transactionsConfig);
        const hasSignUps = isValidConfig(formValues.signUpConfig);
        const hasSessions = isValidConfig(formValues.sessionConfig);

        return hasTransactions || hasSignUps || hasSessions;
      })(),
    };
    
    validation.isComplete = Object.values(validation).every(Boolean);
    return validation;
  }, [formValues]);

  // Reactive field updates for budget/CPM changes
  useEffect(() => {
    if (formValues.budget && formValues.cpm) {
      const impressions = calculateImpressions({
        budget: formValues.budget,
        cpm: formValues.cpm,
      });

      // Only update if different to avoid infinite loops
      if (impressions !== formValues.impressions) {
        form.setFieldsValue({ impressions });
      }
    }
  }, [formValues.budget, formValues.cpm, form, formValues.impressions]);

  // Campaign type changes - set product code
  useEffect(() => {
    if (formValues.type !== undefined && formValues.type !== prevTypeRef.current) {
      let productCode = 'DIS'; // Default
      if (formValues.type === 7) productCode = 'EXT'; // External
      if (formValues.type === 8) productCode = 'CTV'; // Video
      
      form.setFieldsValue({ productCode });
      
      // Handle type change confirmation if there are existing selections - only in edit mode
      const campaigns = formValues.campaigns || [];
      const hasSelections = campaigns.some(c => 
        c?.adGroup && Array.isArray(c.adGroup.adGroupCreatives) && c.adGroup.adGroupCreatives.length > 0
      );
      
      if (mode === 'edit' && prevTypeRef.current !== undefined && hasSelections && !isInitialLoadRef.current && !isCloningRef.current) {
        Modal.confirm({
          title: 'Switch media format?',
          content: 'Changing Campaign Type will clear previously selected Ad Groups/Video Creatives for all line items.',
          okText: 'Clear selections',
          cancelText: 'Keep selections',
          onOk: () => {
            const cleared = campaigns.map((c) => ({ ...c, adGroup: null }));
            form.setFieldsValue({ campaigns: cleared });

            // Continue navigation if there was a pending navigation request
            if (window.pendingNavigationToReview) {
              const campaignOrderId = window.pendingNavigationToReview;
              delete window.pendingNavigationToReview;
              window.location.href = `/campaigns/requester/review/${campaignOrderId}`;
            }
          },
          onCancel: () => {
            form.setFieldsValue({ type: prevTypeRef.current });

            // Continue navigation if there was a pending navigation request
            if (window.pendingNavigationToReview) {
              const campaignOrderId = window.pendingNavigationToReview;
              delete window.pendingNavigationToReview;
              window.location.href = `/campaigns/requester/review/${campaignOrderId}`;
            }
          },
        });
      }
      
      prevTypeRef.current = formValues.type;
    }
  }, [formValues.type, formValues.campaigns, form, mode]);

  // Reset initial load flag after form has been populated
  useEffect(() => {
    // Wait for the form to have some data before considering initial load complete
    if (formValues.type !== undefined || formValues.name || formValues.advertiserId) {
      // Use a small delay to ensure all initial form updates have completed
      const timer = setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [formValues.type, formValues.name, formValues.advertiserId]);

  // Advertiser change handling
  useEffect(() => {
    const currentAdvertiserId = formValues.advertiserId;
    const prevAdvertiserId = prevAdvertiserRef.current;
    
    // Only show the modal in edit mode, not in review mode or during initial load
    if (mode === 'edit' && prevAdvertiserId && prevAdvertiserId !== currentAdvertiserId) {
      const campaigns = formValues.campaigns || [];
      const hasSelections = campaigns.some(c => 
        c?.adGroup && Array.isArray(c.adGroup.adGroupCreatives) && c.adGroup.adGroupCreatives.length > 0
      );
      
      if (hasSelections) {
        Modal.confirm({
          title: 'Switch advertiser?',
          content: 'Changing advertiser will clear previously selected Ad Groups/Video Creatives for all line items.',
          okText: 'Clear selections',
          cancelText: 'Keep selections',
          onOk: () => {
            const cleared = campaigns.map((c) => ({ ...c, adGroup: null }));
            form.setFieldsValue({ campaigns: cleared });
            // Note: setSelectedAdvertiser should be called from the component using this hook
          },
          onCancel: () => {
            form.setFieldsValue({ advertiserId: prevAdvertiserId });
          },
        });
      }
    }
    
    prevAdvertiserRef.current = currentAdvertiserId;
  }, [formValues.advertiserId, formValues.campaigns, form, mode]);

  // Date synchronization for single line item - DISABLED
  // This effect was automatically overwriting line item dates with campaign dates
  // when there was only one line item, preventing users from setting different dates
  // for the line item than the campaign order.
  // useEffect(() => {
  //   const campaigns = formValues.campaigns || [];
  //   if (campaigns.length === 1 && (formValues.startDate || formValues.endDate)) {
  //     const updatedCampaigns = [...campaigns];
  //     const needsUpdate =
  //       updatedCampaigns[0].startDate !== formValues.startDate ||
  //       updatedCampaigns[0].endDate !== formValues.endDate;
  //
  //     if (needsUpdate) {
  //       updatedCampaigns[0] = {
  //         ...updatedCampaigns[0],
  //         startDate: formValues.startDate,
  //         endDate: formValues.endDate,
  //       };
  //
  //       form.setFieldsValue({ campaigns: updatedCampaigns });
  //     }
  //   }
  // }, [formValues.startDate, formValues.endDate, formValues.campaigns, form]);

  // Critical handleValuesChange function that preserves all original business logic
  const handleValuesChange = useCallback((changedValues, allValues) => {
    const { budget, cpm } = allValues;
    const updatedLineItems = [...(allValues.campaigns || [])];

    // Handle advertiser change with modal confirmation
    if ('advertiserId' in changedValues) {
      const selectedAdvertiserObj = childrenOrg?.find((org) => org.id === changedValues.advertiserId);

      if (selectedAdvertiserObj) {
        const prevAdvertiserId = prevAdvertiserRef.current;
        const newAdvertiserId = changedValues.advertiserId;
        const campaigns = form.getFieldValue('campaigns') || [];
        const hasSelections = campaigns.some(
          (c) =>
            c?.adGroup &&
            Array.isArray(c.adGroup.adGroupCreatives) &&
            c.adGroup.adGroupCreatives.length > 0
        );

        if (prevAdvertiserId !== undefined && prevAdvertiserId !== newAdvertiserId && hasSelections) {
          Modal.confirm({
            title: 'Switch advertiser?',
            content:
              'Changing advertiser will clear previously selected Ad Groups/Video Creatives for all line items.',
            okText: 'Clear selections',
            cancelText: 'Keep selections',
            onOk: () => {
              const cleared = campaigns.map((c) => ({ ...c, adGroup: null }));
              form.setFieldsValue({ campaigns: cleared });
              setSelectedAdvertiser(selectedAdvertiserObj);
              prevAdvertiserRef.current = newAdvertiserId;
            },
            onCancel: () => {
              form.setFieldsValue({ advertiserId: prevAdvertiserId });
            },
          });
        } else {
          setSelectedAdvertiser(selectedAdvertiserObj);
          prevAdvertiserRef.current = newAdvertiserId;
        }
      }
    }

    // Handle campaign type change with modal confirmation
    if ('type' in changedValues) {
      let productCode = 'DIS'; // Default
      if (changedValues.type === 7) {
        productCode = 'EXT'; // External
      } else if (changedValues.type === 8) {
        productCode = 'CTV'; // Video
      }
      form.setFieldsValue({ productCode });

      const prevType = prevTypeRef.current;
      const newType = changedValues.type;
      const campaigns = form.getFieldValue('campaigns') || [];
      const hasSelections = campaigns.some(
        (c) =>
          c?.adGroup &&
          Array.isArray(c.adGroup.adGroupCreatives) &&
          c.adGroup.adGroupCreatives.length > 0
      );

      if (prevType !== undefined && prevType !== newType && hasSelections && !isCloningRef.current) {
        Modal.confirm({
          title: 'Switch media format?',
          content:
            'Changing Campaign Type will clear previously selected Ad Groups/Video Creatives for all line items.',
          okText: 'Clear selections',
          cancelText: 'Keep selections',
          onOk: () => {
            const cleared = campaigns.map((c) => ({ ...c, adGroup: null }));
            form.setFieldsValue({ campaigns: cleared });
            prevTypeRef.current = newType;
          },
          onCancel: () => {
            form.setFieldsValue({ type: prevType });
          },
        });
      } else {
        prevTypeRef.current = newType;
      }
    }

    // Handle budget and CPM changes
    if ('budget' in changedValues || 'cpm' in changedValues) {
      form.setFieldsValue({
        impressions: calculateImpressions({
          budget,
          cpm: cpm || (selectedAdvertiser ? Math.max(Number(selectedAdvertiser.cpm.replace(/,/g, '')), 1) : 7),
        }),
      });
    }

    // Handle date changes - sync with line items if only one exists
    if ('startDate' in changedValues || 'endDate' in changedValues) {
      const campaigns = form.getFieldValue('campaigns');
      if (campaigns && campaigns.length === 1) {
        form.setFieldsValue({
          campaigns: [
            {
              ...campaigns[0],
              startDate: allValues.startDate,
              endDate: allValues.endDate,
            },
          ],
        });
      }
      form.validateFields();
    }

    // Handle line item budget changes
    if ('campaigns' in changedValues) {
      const lineItemIndex = Object.keys(changedValues.campaigns)[0];
      const changedField = Object.keys(changedValues.campaigns[lineItemIndex])[0];
      const { cpm, budgetTotal, budgetPercentage } = updatedLineItems[lineItemIndex];

      if (changedField === 'budgetTotal') {
        updatedLineItems[lineItemIndex] = {
          ...updatedLineItems[lineItemIndex],
          budgetPercentage: Math.round((budgetTotal / budget) * 100),
          impressions: calculateImpressions({
            cpm: cpm || 7,
            budget: Number(budgetTotal),
          }),
        };
      }

      if (changedField === 'budgetPercentage') {
        updatedLineItems[lineItemIndex] = {
          ...updatedLineItems[lineItemIndex],
          impressions: calculateImpressions({
            cpm: cpm || 7,
            budget: (Number(budgetPercentage) / 100) * Number(budget),
          }),
          budgetTotal: (Number(budgetPercentage) / 100) * Number(budget),
        };
      }

      form.setFieldsValue({ campaigns: updatedLineItems });
    }
  }, [form, childrenOrg, selectedAdvertiser, setSelectedAdvertiser]);

  // Handle CPM validation on blur - critical functionality from original component
  const handleCpmBlur = useCallback((value, index = null) => {
    if (value <= 0 && selectedAdvertiser) {
      let advertiserCpm = Number(selectedAdvertiser.cpm.replace(/,/g, ''));
      
      // Fallback to 1 if advertiser's CPM is 0 or negative
      if (advertiserCpm <= 0) {
        advertiserCpm = 1;
      }
      
      // Notify user about the CPM correction
      if (index !== null) {
        message.info(`Line Item ${index + 1} CPM has been automatically corrected from ${value} to ${advertiserCpm} (advertiser's default rate)`, 3);
      } else {
        message.info(`Campaign CPM has been automatically corrected from ${value} to ${advertiserCpm} (advertiser's default rate)`, 3);
      }
      
      // Update the form with the corrected CPM value
      if (index !== null) {
        // Update line item CPM
        const campaigns = form.getFieldValue('campaigns');
        if (campaigns && campaigns[index]) {
          const updatedCampaigns = [...campaigns];
          updatedCampaigns[index] = {
            ...updatedCampaigns[index],
            cpm: advertiserCpm,
            impressions: calculateImpressions({
              budget: Number(updatedCampaigns[index].budgetTotal) || 0,
              cpm: advertiserCpm,
            }),
          };
          form.setFieldsValue({ campaigns: updatedCampaigns });
        }
      } else {
        // Update campaign-level CPM
        form.setFieldsValue({
          cpm: advertiserCpm,
          impressions: calculateImpressions({
            budget: form.getFieldValue('budget'),
            cpm: advertiserCpm,
          }),
        });
        
        // Also update all line items with the corrected CPM
        const campaigns = form.getFieldValue('campaigns');
        if (campaigns && campaigns.length > 0) {
          const updatedCampaigns = campaigns.map((campaign) => ({
            ...campaign,
            cpm: advertiserCpm,
            impressions: calculateImpressions({
              budget: Number(campaign.budgetTotal) || 0,
              cpm: advertiserCpm,
            }),
          }));
          form.setFieldsValue({ campaigns: updatedCampaigns });
        }
      }
    }
  }, [form, selectedAdvertiser]);

  // Helper methods
  const helpers = {
    form, // Include form reference for legacy compatibility
    handleValuesChange, // Expose the handleValuesChange function
    handleCpmBlur, // Expose the CPM correction function
    updateField: useCallback((name, value) => {
      form.setFieldsValue({ [name]: value });
    }, [form]),
    
    updateNestedField: useCallback((path, value) => {
      if (Array.isArray(path)) {
        // Handle array paths like ['campaigns', 0, 'name']
        const updateObj = {};
        let current = updateObj;
        
        for (let i = 0; i < path.length - 1; i++) {
          current[path[i]] = current[path[i]] || {};
          current = current[path[i]];
        }
        current[path[path.length - 1]] = value;
        
        form.setFieldsValue(updateObj);
      } else {
        // Handle string paths
        form.setFieldsValue({ [path]: value });
      }
    }, [form]),
    
    getFieldValue: useCallback((name) => form.getFieldValue(name), [form]),
    
    getFieldsValue: useCallback((fields) => form.getFieldsValue(fields), [form]),
    
    setFieldsValue: useCallback((values) => form.setFieldsValue(values), [form]),
    
    resetFormSection: useCallback((section) => {
      const resetValues = {};
      if (section === 'campaigns') {
        resetValues.campaigns = [];
      }
      form.setFieldsValue(resetValues);
    }, [form]),
    
    correctCpm: handleCpmBlur, // Legacy alias
    
    validateFields: useCallback(async (fields) => {
      try {
        await form.validateFields(fields);
        return true;
      } catch (error) {
        return false;
      }
    }, [form]),
    
    // Helper for line item budget calculations
    updateLineItemBudget: useCallback((index, budgetChange) => {
      const campaigns = form.getFieldValue('campaigns') || [];
      const budget = form.getFieldValue('budget') || 0;
      const updatedCampaigns = [...campaigns];
      
      if (budgetChange.budgetTotal !== undefined) {
        const budgetTotal = budgetChange.budgetTotal;
        updatedCampaigns[index] = {
          ...updatedCampaigns[index],
          budgetTotal: budgetTotal,
          budgetPercentage: budget > 0 ? Math.round((budgetTotal / budget) * 100) : 0,
          impressions: calculateImpressions({
            cpm: updatedCampaigns[index].cpm || 7,
            budget: Number(budgetTotal),
          }),
        };
      }
      
      if (budgetChange.budgetPercentage !== undefined) {
        const percentage = budgetChange.budgetPercentage;
        const budgetTotal = (Number(percentage) / 100) * Number(budget);
        updatedCampaigns[index] = {
          ...updatedCampaigns[index],
          budgetTotal: budgetTotal,
          budgetPercentage: percentage,
          impressions: calculateImpressions({
            cpm: updatedCampaigns[index].cpm || 7,
            budget: budgetTotal,
          }),
        };
      }
      
      form.setFieldsValue({ campaigns: updatedCampaigns });
    }, [form]),

    // Handle cloning operations without triggering confirmation popups
    handleCloningOperation: useCallback((cloningFunction) => {
      // Set cloning state to bypass popups
      isCloningRef.current = true;

      try {
        // Execute the cloning operation
        const result = cloningFunction();

        // Force form refresh after cloning to ensure UI updates
        setTimeout(() => {
          console.log('=== FORCING FORM WATCH REFRESH AFTER CLONING ===');
          setForceUpdateKey(prev => prev + 1);
          form.validateFields().catch(() => {
            // Ignore validation errors, we just want to trigger re-render
          });

          // Reset cloning state
          isCloningRef.current = false;
        }, 200);

        return result;
      } catch (error) {
        // Reset cloning state on error
        isCloningRef.current = false;
        throw error;
      }
    }, [form, setForceUpdateKey]),

    // Force form refresh for UI synchronization issues
    forceRefresh: useCallback(() => {
      console.log('=== MANUAL FORM REFRESH TRIGGERED ===');
      setForceUpdateKey(prev => prev + 1);
      form.validateFields().catch(() => {
        // Ignore validation errors, we just want to trigger re-render
      });
    }, [form, setForceUpdateKey]),
  };

  return {
    form,
    formValues,
    derivedState,
    validationState,
    helpers,
    forceUpdateKey, // Include this to trigger re-renders when needed
  };
};